import { Component, type ErrorInfo, type ReactNode } from "react";

/** One broken panel must not take the whole screen with it.
 *
 * On 23 Sep 2026 a figure arrived as a string, one table called .toFixed()
 * on it, and React unmounted everything: the Analytics page loaded and then
 * went white, with nothing on screen to say why. A page that says what broke
 * - and still has its navigation - is the difference between "the system is
 * down" and "this panel is broken, use the rest".
 */
type Props = { children: ReactNode; where?: string };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // the console is where a developer looks first; keep the component trail
    console.error("screen crashed", this.props.where ?? "", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="banner bad stack" style={{ margin: 16 }}>
        <b>This screen hit a problem{this.props.where ? ` (${this.props.where})` : ""}.</b>
        <span className="dim" style={{ fontSize: 12.5 }}>
          Nothing has changed to your sending - this is the screen only. The rest of the console
          still works; the details are in the browser console.
        </span>
        <div className="mono" style={{ fontSize: 12, opacity: 0.8 }}>{String(error.message || error)}</div>
        <div className="row" style={{ gap: 8, marginTop: 6 }}>
          <button className="btn p" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
          <button className="btn" onClick={() => window.location.reload()}>
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
